import { User } from '@apis/index'
import icons from '@assets/icons'
import Button from '@components/button'
import Column from '@components/column'
import Image from '@components/image'
import SvgImage from '@components/svg'
import Typography from '@components/typography'
import useWindowSize from '@hooks/useWindow'
import { Constant, Helper } from '@utils/index'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  ContactInfo,
  Menu,
  MenuItem,
  Name,
  ProfileCard,
  SideBarContainer,
  SubscriptionActions,
  SubscriptionCard,
} from './style'
import { useEffect, useState } from 'react'
import PopupToast from '@components/custom-toast'
import initialState from '@store/initialState'
import { Actions, store } from '@store/index'
import { format } from 'date-fns'
import { requestPermission } from '@utils/pushNotification'

const SideBar = ({ setSideBar, setDeactivateVisible }) => {
  const { width } = useWindowSize()
  const [deactivateLoading, setDeactivateLoading] = useState(false)

  const [visible, setVisible] = useState(false)
  const navigate = useNavigate()
  const { data: user } = useSelector((state: any) => state.user)
  const mobile = width <= 1024
  const handleClick = (href: any) => {
    navigate(href)
    width >= 1024 && setSideBar(false)
  }
  const [fcmToken, setFcmToken] = useState(null)
  const fetchFCMToken = async () => {
    try {
      const fcmToken = await requestPermission()
      setFcmToken(fcmToken)
    } catch (error) {
      console.error('Error fetching FCM token:', error)
    }
  }

  useEffect(() => {
    fetchFCMToken()
  }, [])

  const onLogout = async () => {
    const payload: any = {}
    if (fcmToken) {
      payload.deviceToken = fcmToken
    }
    await User.logout(payload)
      .then((resp) => {
        if (resp) {
          Object.keys(initialState).forEach((key) => {
            store.dispatch(Actions.reset(key as keyof typeof initialState))
          })
          Helper.removeUser()
          navigate('/')
        }
      })
      .catch(() => {})
  }
  const avatar = user?.image
    ? `${process.env.BUCKET_URL}/${user?.image}`
    : Constant.defaultUser

  const primaryMenu: {
    src: 'manageProfile' | 'passwordIcon' | 'bookIcon' | 'historyIcon'
    value: string
    href: string
  }[] = [
    {
      src: 'manageProfile',
      value: 'Manage Profile',
      href: '/books/manage-profile',
    },
    {
      src: 'passwordIcon',
      value: 'Password & Security',
      href: '/books/change-password',
    },
    { src: 'bookIcon', value: 'Book Listing', href: '/books/my-books' },
    { src: 'historyIcon', value: 'History', href: '/books/history' },
  ]

  const secondaryMenu: {
    src: keyof typeof icons
    value: string
    href?: string
    onClick?: () => void
  }[] = [
    { src: 'aboutIcon', value: 'About Us', href: '/books/about-us' },
    {
      src: 'privacyIcon',
      value: 'Privacy Policy',
      href: '/books/privacy-policy',
    },
    {
      src: 'termsIcon',
      value: 'Terms & Conditions',
      href: '/books/terms-and-condition',
    },
    {
      src: 'cardSwipe',
      value: 'Refund Policy',
      href: '/books/refund-policy',
    },
    { src: 'supportIcon', value: 'Support', href: '/books/support' },
    {
      src: 'deactivateIcon',
      value: 'Delete Account',
      onClick: () => {
        !mobile && setSideBar(false)
        !mobile && setDeactivateVisible(true)
        !!mobile && setVisible(true)
      },
    },
  ]
  const handleDeactivate = async () => {
    setDeactivateLoading(true)
    const payload = { isActive: false }
    await User.updateStatus(payload, user?.id)
      .then((resp) => {
        if (resp) {
          Object.keys(initialState).forEach((key) => {
            store.dispatch(Actions.reset(key as keyof typeof initialState))
          })
          Helper.removeUser()
          navigate('/')
        }
      })
      .catch(() => {})
      .finally(() => {
        setDeactivateLoading(false)
      })
  }

  return (
    <>
      <SideBarContainer>
        <ProfileCard>
          <Image src={avatar} radius={50} height={80} width={80} />
          <Column>
            <Name>{user?.name}</Name>
            <ContactInfo>
              <SvgImage src="phoneIcon" /> {user?.phoneNo}
            </ContactInfo>
            <ContactInfo>
              <SvgImage src="locationIcon" /> {user?.landmark}
            </ContactInfo>
          </Column>
        </ProfileCard>
        {/* uncomment for app */}
        <SubscriptionCard
          onClick={() => {
            if (user?.status === 'Subscribed') {
              return
            }
            navigate('/books/subscription')
            width >= 1024 && setSideBar(false)
          }}
        >
          <Typography
            content={
              user?.status === 'Subscribed' ? user?.status : 'Subscription'
            }
            size={14}
            tc="primary"
            weigth={500}
          />
          <Typography
            size={12}
            tc="greyText"
            weigth={400}
            content={
              user?.status === 'Subscribed'
                ? 'You are already subscribed on the platform. Now you can Lend & Borrow Books on Platform'
                : 'You need to purchase a subscription to Lend & Borrow Books on the Platform.'
            }
          />
          <SubscriptionActions>
            <>
              {user?.status === 'Subscribed' ? (
                <>
                  {!!user?.transactionData?.expiresIn && (
                    <Button
                      title={`Expires at ${format(user?.transactionData?.expiresIn, 'dd MMM ,yyyy')}`}
                      styleButton="padding:0px 20px"
                      disabled
                      height={26}
                      radius={4}
                    />
                  )}
                </>
              ) : (
                <>
                  <Button
                    title="Rs 500/year"
                    styleButton="padding:0px 20px"
                    disabled
                    height={26}
                    bg="red300"
                    radius={4}
                  />
                  <Button
                    title="Upgrade"
                    height={26}
                    styleButton="padding:0px 20px"
                    radius={4}
                    onClick={() => {
                      navigate('/books/subscription')
                      width >= 1024 && setSideBar(false)
                    }}
                  />
                </>
              )}
            </>
          </SubscriptionActions>
        </SubscriptionCard>

        <Menu>
          {primaryMenu.map(({ src, value, href }) => (
            <MenuItem key={src} onClick={() => handleClick(href)}>
              <SvgImage src={src} /> {value}
            </MenuItem>
          ))}
        </Menu>

        <Menu>
          {secondaryMenu.map(({ src, value, href, onClick }) => (
            <MenuItem
              key={src}
              onClick={() => {
                !!onClick && onClick()
                !!href && handleClick(href)
              }}
            >
              <SvgImage src={src} /> {value}
            </MenuItem>
          ))}
          <MenuItem onClick={onLogout}>
            {' '}
            <SvgImage src="logoutIcon" /> Log Out
          </MenuItem>
        </Menu>
      </SideBarContainer>
      {mobile && (
        <PopupToast
          isOpen={visible}
          content="Are you sure you want to delete your account ? All the data associated with this account will be permanently deleted"
          onClick={handleDeactivate}
          onClose={() => {
            setVisible(false)
          }}
          image={'deactivate'}
          buttonText="Yes"
          cancelText={'No'}
          loading={deactivateLoading}
        />
      )}
    </>
  )
}

export default SideBar
